import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import MenuItem from '@material-ui/core/MenuItem';
import MoreVertIcon from '@material-ui/icons/MoreVert';

// CONSTANTS
import { ROUTE_SHOWS_AND_MOVIES_MOVIE, ROUTE_SHOWS_AND_MOVIES_SHOW } from '@/constants/routes';

// UTILITY
import { getLink, routeEnhance } from '@/utility/routes';

// HOOKS
import useActions from '@/hooks/useActions';

// STORE
import { NotifActions } from '@/store/actions';

// STYLES
import { StyledIconButton, MenuStyled } from './styles';
import { shareInfo } from '@/utility/common';
import { ContentType } from '@/constants/watchList';

const MoreActionsNav = ({ data, watchlistId, addToWatchList, removeFromWatchList }) => {
  const [t] = useTranslation();
  const [moreNavEl, setMoreNavEl] = React.useState(null);

  const [pushSuccessNotif] = useActions([
    message =>
      NotifActions.pushNotification.action({
        message,
        options: {
          variant: 'success',
        },
      }),
  ]);

  const isShow = data.type === ContentType.SHOW;
  const inWatchlist =
    !!watchlistId && !!data.watchlists && !!data.watchlists.find(item => item.id === +watchlistId);

  const handleMoreNavIconClick = e => {
    e.stopPropagation();

    setMoreNavEl(e.currentTarget);
  };

  const handleAddToWatchlist = () => {
    addToWatchList(data);
    setMoreNavEl(null);
  };

  const handleRemoveItem = () => {
    removeFromWatchList(data, [+watchlistId]);
    setMoreNavEl(null);
  };

  const handleShareClick = () => {
    const movieLink = getLink(ROUTE_SHOWS_AND_MOVIES_MOVIE, { movie_id: data.slug || data.id });
    const showLink = getLink(ROUTE_SHOWS_AND_MOVIES_SHOW, { show_id: data.slug || data.id });
    const contentRoute = isShow ? showLink : movieLink;
    const enhancedRoute = routeEnhance(contentRoute);
    const sharedLink = `${window.location.origin}${enhancedRoute.pathname}`;

    shareInfo({ url: sharedLink });

    setMoreNavEl(null);
    pushSuccessNotif(t('notification.successfullyCopied'));
  };

  let navItems = [
    {
      icon: 'plus',
      action: handleAddToWatchlist,
      action_name: 'rename',
      text: t('buttons.addToWatchlist'),
    },
    {
      icon: 'remove',
      action: handleRemoveItem,
      action_name: 'delete',
      text: t('buttons.removeItem'),
    },
    {
      icon: 'share-2',
      action: handleShareClick,
      action_name: 'share',
      text: t('buttons.share'),
    },
  ];

  navItems = navItems.filter(item => (!inWatchlist ? item.action_name !== 'delete' : item));

  return (
    <>
      <StyledIconButton
        className="content-card__more-btn"
        aria-label="more"
        onClick={handleMoreNavIconClick}
      >
        <MoreVertIcon />
      </StyledIconButton>
      <MenuStyled
        className="more-nav"
        getContentAnchorEl={null}
        anchorEl={moreNavEl}
        keepMounted
        open={Boolean(moreNavEl)}
        onClose={() => setMoreNavEl(null)}
      >
        {navItems.map((item, i) => (
          <MenuItem onClick={item.action} key={i}>
            {item.icon && <i className={`more-nav__icon icon-${item.icon}`} />}
            {item.text}
          </MenuItem>
        ))}
      </MenuStyled>
    </>
  );
};

MoreActionsNav.defaultProps = {
  addToWatchList: () => {},
  removeFromWatchList: () => {},
};

MoreActionsNav.propTypes = {
  data: PropTypes.object.isRequired,
  watchlistId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  addToWatchList: PropTypes.func,
  removeFromWatchList: PropTypes.func,
};

export default React.memo(MoreActionsNav);
